import { ArrowCircleDownIcon, ArrowCircleRightIcon } from "@heroicons/react/outline";
import { ExclamationCircleIcon } from "@heroicons/react/solid";
import moment, { Moment } from "moment";
import { Fragment, useEffect, useState } from "react";
import TimesheetSummary from "../../api/models/timesheetSummary";
import TimesheetWeek from "../../api/models/timesheetWeek";
import WeekItem from "../../api/models/timesheetWeekItem";
import TimesheetWeekTask from "../../api/models/TimesheetWeekTask";
import { daysOfWeek, getTimeDisplay } from "../../common/utils";
import { TimesheetFlagStatus, TimesheetStatus } from '../../common/constants';
import EditEntry from "../logTimeModal/types/editEntry";
import TimesheetGroupedDistrictItem from "../../api/models/TimesheetGroupedDistrictItem";
import TimesheetGroupedSchoolItem from "../../api/models/TimesheetGroupedSchoolItem";
import TimesheetGroupTitle from "../../api/models/TimesheetGroupTitle";
import TimesheetDistrict from "../../api/models/timesheetDistrict";

type Props = {
    week: TimesheetWeek,
    timesheet?: TimesheetSummary,
    displayFlags?: boolean,
    allowEdit: boolean,
    editEntry: (editEntry: EditEntry) => void;
    districts?: TimesheetDistrict[];
}

let previousWeek: string = "";

const TimesheetWeekEntries = ({ week, timesheet = undefined, displayFlags = false, allowEdit, editEntry, districts = [] }: Props) => {
    const [expandedWeek, setExpandedWeek] = useState<String>();
    const [expandAll, setExpandAll] = useState<boolean>(false);
    const [groupedWeek, setGroupedWeek] = useState<TimesheetGroupedDistrictItem<WeekItem>[]>();

    /**
     * Returns whether a given day is a weekend day (Saturday or Sunday)
     */
    const isWeekendDay = (day: Moment): boolean => {
        const dayOfWeek = day.isoWeekday();
        return dayOfWeek === 6 || dayOfWeek === 7; // 6 = Saturday, 7 = Sunday
    }

    /**
     * Returns whether a district has existing weekend entries in the current week
     */
    const hasWeekendEntries = (districtId?: number): boolean => {
        if (!week?.items) return false;
        const items = districtId ? week.items.filter(i => i.districtId === districtId) : week.items;
        return items.some(item =>
            item.tasks?.some(task =>
                task.entries?.some(entry => {
                    const d = moment(entry.dateOfEntry).isoWeekday();
                    return (d === 6 || d === 7) && (entry.minutes ?? 0) > 0;
                })
            )
        );
    }

    /**
     * Returns whether weekends should be shown for a given district.
     * Show if billing is enabled OR if there are existing weekend entries.
     */
    const shouldShowWeekend = (districtId?: number): boolean => {
        if (!districtId || !districts || districts.length === 0) return true;
        const district = districts.find(d => d.districtId === districtId);
        if (district?.enableWeekendBilling) return true;
        return hasWeekendEntries(districtId);
    }

    /**
     * Returns whether any district in the current view has weekend billing enabled or has existing weekend entries
     */
    const anyDistrictShowsWeekend = (): boolean => {
        if (!districts || districts.length === 0) return true;
        const weekDistrictIds = [...new Set(week?.items?.map(item => item.districtId) ?? [])];
        return weekDistrictIds.some(id => shouldShowWeekend(id));
    }

    const createGroupedWeekItemsModel = (): TimesheetGroupedDistrictItem<WeekItem>[] => {
        let groupedWeek: TimesheetGroupedDistrictItem<WeekItem>[] = [];
        week.items.forEach(item => {
            let programTitles: TimesheetGroupTitle[] = [];

            let programTasks = item.tasks.filter(x => x.programName);
            if (programTasks.length > 0) {
                programTasks.forEach(t => {
                    if (!programTitles.find(x => x.name === t.programName))
                        programTitles.push({ name: t.programName.toString(), isProgram: true });
                })
            }

            if (programTasks.length !== item.tasks.length)
                programTitles.push({ name: item.description, isProgram: false });

            programTitles.forEach(pt => {
                var districtGroup = groupedWeek.find(x => x.title === pt.name);
                if (!districtGroup) {
                    let districtTemp: TimesheetGroupedDistrictItem<WeekItem> = {
                        title: pt.name.toString(),
                        districtId: item.districtId,
                        schools: [],
                    }

                    districtGroup = districtTemp;
                    groupedWeek.push(districtGroup);
                }

                var schoolGroup = districtGroup.schools?.find(x => x.title === item.subTitle);
                if (!schoolGroup) {
                    let schoolTemp: TimesheetGroupedSchoolItem<WeekItem> = {
                        title: item.subTitle,
                        items: []
                    }

                    schoolGroup = schoolTemp;
                    districtGroup.schools?.push(schoolGroup);
                }

                if (item.tasks.length > 1) {
                    let tempItem = { ...item };
                    tempItem.tasks = tempItem.tasks.filter(x => x.programName === (pt.isProgram ? pt.name : null));

                    schoolGroup.items?.push(tempItem);
                } else {
                    schoolGroup.items?.push(item);
                }
            })
        })

        return groupedWeek;
    }

    useEffect(() => {
        setGroupedWeek(createGroupedWeekItemsModel());
    }, [week])

    //check if the selected week changed to reset the expanded week
    if (typeof week.firstDayOfWeek === "undefined" || moment(week.firstDayOfWeek).toString() !== previousWeek) {
        //note: I'm not sure why the state won't update unless it happens after the render finishes.. ~tdepole
        setTimeout(() => {
            setExpandedWeek('');
            setExpandAll(false);
        }, 100);
        previousWeek = moment(week.firstDayOfWeek).toString();
    }

    /**
     * Returns a sum of all hours worked on a given day for all entries 
     * @param day 
     * @param weeks 
     * @returns number of mins
    */
    const getTotalsForWeek = (day: Moment, weeks: WeekItem[]) => {
        if (weeks?.length <= 0)
            return 0;

        let total = 0
        weeks.forEach(w => {
            let tt = getTotalsForDay(day, w, true);
            if (typeof tt == 'number') {
                total += tt;
            }
        });

        return total;
    }

    const getTotalsForDistrict = (districtSchools: TimesheetGroupedDistrictItem<WeekItem>[]) => {
        if (districtSchools?.length <= 0)
            return 0;

        let total = 0;
        districtSchools.forEach(s => {
            total += getTotalsForSchool(s);
        })

        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });

        return formatter.format(total / 60);
    }

    const getTotalsForSchool = (school: TimesheetGroupedSchoolItem<WeekItem>) => {
        if (school?.items && school.items.length <= 0)
            return 0;

        let total = 0;
        school.items?.forEach(s => {
            s.tasks.forEach(t => {
                t.entries.forEach(e => {
                    total += e.minutes ?? 0;
                })
            })
        })

        return total;
    }

    const getTotalsForDistrictWeek = (day: Moment, districtSchools: TimesheetGroupedSchoolItem<WeekItem>[]) => {
        if (districtSchools?.length <= 0)
            return 0;

        let total = 0
        districtSchools.forEach(s => {
            s.items?.forEach(e => {
                let tt = getTotalsForDay(day, e, true);
                if (typeof tt == 'number') {
                    total += tt;
                }
            })
        });

        return total;
    }

    /**
     * Expand/Collapse the entry for a student
     * @param index 
     */
    const toggleWeek = (index: string) => {
        if (expandAll) {
            setExpandAll(false);
            setExpandedWeek('');
            return;
        }

        setExpandedWeek((expandedWeek === index) ? '' : index);
    }


    /**
     * Returns a sum of all hours worked on a given day for a single student/entry 
     * @param day 
     * @param weeks 
     * @returns number of mins
     */
    const getTotalsForDay = (day: Moment, week: WeekItem, totalOnly = false) => {
        let foundAbsence = false;
        if (week.tasks?.length <= 0)
            return 0;

        let total = 0;
        week.tasks.forEach(t => {
            if (t.taskTypeName === "Absence") {
                if (t.entries?.find(e => moment(e.dateOfEntry).isSame(day)) != null) {
                    foundAbsence = true;
                }
            }
            const m = t.entries?.find(e => moment(e.dateOfEntry).isSame(day));
            total += m && m !== undefined && m.minutes && m.minutes !== undefined ? m?.minutes?.valueOf() : 0 ?? 0
        });

        return !totalOnly && foundAbsence ? "A" : total;
    }

    /**
     * Returns true if any of the entries in a given week are flagged
     * @param items 
     * @param day 
     * @returns boolean
     */
    const isWeekFlagged = (items: WeekItem[], day: Moment) => {
        let flags: any[] = [];

        if (displayFlags) {
            items.forEach((item) => {
                item.tasks.forEach((task) => {
                    const timesheetEntryFlags = task.entries.find(e => moment(e.dateOfEntry).isSame(day))?.timesheetEntryFlags!;
                    if (timesheetEntryFlags !== undefined && timesheetEntryFlags.length > 0) flags.push(task);
                });
            });
        }

        return flags.length > 0;
    }

    const isDistrictWeekFlagges = (districtSchools: TimesheetGroupedSchoolItem<WeekItem>[], day: Moment) => {
        let isFlagged: boolean = false;

        if (displayFlags) {
            for (let i = 0; i < districtSchools.length; i++) {
                isFlagged = isWeekFlagged(districtSchools[i].items!, day);
                if (isFlagged)
                    break;
            }
        }

        return isFlagged;
    }

    /**
     * Returns true if a task has been flagged
     * @param item
     * @returns boolean
     */
    const isTaskFlagged = (item: WeekItem) => {
        if (displayFlags) {
            let isFlagged = false;

            daysOfWeek(week.firstDayOfWeek).forEach((day, weekIndex) => {
                isFlagged = isFlagged === false ? isDayFlagged(day, item) : isFlagged;
            });

            return isFlagged;
        }

        return false;
    }

    /**
     * Returns a boolean determining whether the day has flags
     * @param day 
     * @param weeks 
     * @returns boolean
     */
    const isDayFlagged = (day: Moment, item: WeekItem) => {
        let flags: any[] = [];

        if (displayFlags) {
            item.tasks.forEach((task) => {
                const timesheetEntryFlags = task.entries.find(e => moment(e.dateOfEntry).isSame(day))?.timesheetEntryFlags!;
                let isRejected = timesheetEntryFlags ? timesheetEntryFlags.filter(f => f.timesheetFlagStatusId === TimesheetFlagStatus.Rejected || f.timesheetFlagStatusId === TimesheetFlagStatus.Generated).length > 0 : false;
                if (timesheetEntryFlags !== undefined && timesheetEntryFlags.length > 0 && isRejected) flags.push(task);

                /*const timesheetEntryFlags = task.entries.find(e => moment(e.dateOfEntry).isSame(day))?.timesheetEntryFlags!;
                if (timesheetEntryFlags !== undefined && timesheetEntryFlags.length > 0) flags.push(task);*/
            });
        }

        return flags.length > 0;
    }

    /**
     * Returns whether a day within an entry is flagged
     * @param day 
     * @param task 
     * @returns boolean
     */
    const isEntryDayFlagged = (day: moment.Moment, task: TimesheetWeekTask) => {
        const timesheetEntryFlags = task?.entries?.find(e => moment(e.dateOfEntry).isSame(day))?.timesheetEntryFlags;
        //let isRejected = timesheetEntryFlags ? timesheetEntryFlags.filter(f => f.timesheetFlagStatusId === TimesheetFlagStatus.Rejected).length > 0 : false;

        return displayFlags && timesheetEntryFlags !== null &&
            timesheetEntryFlags !== undefined &&
            timesheetEntryFlags.length > 0;
    }

    /**
     * Loops through an entry and if any of the associated flags are rejected, it returns the rejected color
     * @param entry 
     * @returns string
     */
    const getColor = (entry: any = undefined) => {
        const colorRed = 'text-red-600';
        const colorOrange = 'text-orange-500';
        let color = colorRed;

        if (entry !== undefined) {
            switch (timesheet?.status?.timesheetStatusId) {
                case TimesheetStatus.Rejected:
                    color = colorOrange;
                    break;
                case TimesheetStatus.Submitted:
                    let isRejected = false;

                    //Loop through the flags for this entry
                    entry.timesheetEntryFlags.forEach((flag: any) => {
                        if (flag.timesheetFlagStatus.timesheetFlagStatusId === TimesheetFlagStatus.Rejected) isRejected = true;
                    });

                    color = isRejected ? colorOrange : colorRed;
                    break;
            }
        }

        return color;
    }

    return (
        <Fragment>
            {/* Header of timesheet */}
            <div className='flex justify-end px-1.5'>
                {/* Days of week */}
                {daysOfWeek(week?.firstDayOfWeek).map((day, i) => (
                    <div key={i} className={`font-bold text-center w-20 shrink-0 ${!anyDistrictShowsWeekend() && isWeekendDay(day) ? 'hidden' : ''}`}>
                        <div>{day.format('ddd')}</div>
                        <div>{day.format('M/D')}</div>
                    </div>
                ))}
                <div key={`header-actions`} className='w-8 font-bold text-center header-actions'></div>
            </div>

            {/* If no timesheets */}
            {week?.items?.length <= 0 &&
                <div className='text-center text-gray-500'>
                    No timesheets in the selected range
                </div>
            }

            {/* Total Minutes */}
            {week?.items?.length > 0 &&
                <div className='flex justify-end px-1.5 pt-0.5 pb-1/2'>
                    <div className='font-bold shrink-0'>Total Daily Minutes</div>
                    {/* Days of week */}
                    {daysOfWeek(week?.firstDayOfWeek).map((day, i) => (
                        <div key={i} className={`w-20 shrink-0 italic text-center ${!anyDistrictShowsWeekend() && isWeekendDay(day) ? 'hidden' : ''}`}>
                            <span className={isWeekFlagged(week.items, day) ? getColor() : ""}>
                                {getTotalsForWeek(day, week.items)}</span>
                        </div>
                    ))}
                    <div key={`header-actions-minutes`} className='w-8 font-bold text-center'></div>
                </div>
            }

            {/* expand all link */}
            {week?.items?.length > 0 &&
                <div className='pb-2 pl-2 text-left'>
                    <span className='cursor-pointer text-ptsblue-normal hover:underline' onClick={() => { setExpandAll(!expandAll); setExpandedWeek(''); }}> {expandAll ? "Collpase all" : "Expand All"} </span>
                </div>
            }

            {/* The timesheets */}
            {groupedWeek && groupedWeek?.sort((a, b) => a.title?.localeCompare(b.title)).map((district, districtIndex) => {
                const districtShowWeekend = shouldShowWeekend(district.districtId);
                if (district.schools?.length! > 0) {
                    return (
                        <div key={districtIndex}>
                            <h1><span style={{ fontWeight: 'bold' }}>{district.title}</span> (Total Time {getTotalsForDistrict(district?.schools!)}hrs.)</h1>
                            <br />
                            {district.schools?.sort((a, b) => a.title?.localeCompare(b.title)).map((school, schoolIndex) => {
                                if (school.items?.length! > 0) {
                                    const itemIndex = `${districtIndex}_${schoolIndex}`;
                                    return (
                                        <div key={itemIndex}>
                                            <h3>{school.title}</h3>
                                            <br />
                                            {school.items?.sort((a, b) => a.title?.localeCompare(b.title)).map((entry, entryIndex) => {
                                                const itemIndex = `${districtIndex}_${schoolIndex}_${entryIndex}`;
                                                return (
                                                    <div key={itemIndex} className='bg-gray-100 rounded-lg p-0.5 px-1.5 mb-3'>
                                                        {/* Summary row */}
                                                        <div className='flex justify-end items-center my-4 cursor-pointer' onClick={() => toggleWeek(itemIndex)}>
                                                            <div className='flex items-start flex-1 min-w-0'>
                                                                <div>
                                                                    {(expandedWeek === itemIndex || expandAll) && (
                                                                        <ArrowCircleDownIcon className='w-6 mt-1 mr-1 text-gray-800' />
                                                                    )}

                                                                    {(expandedWeek !== itemIndex && !expandAll) && (
                                                                        <ArrowCircleRightIcon className='w-6 mt-1 mr-1 text-gray-800' />
                                                                    )}
                                                                </div>
                                                                <div>
                                                                    <div className='text-base font-bold'>{entry.title}</div>
                                                                    <div className='text-xs'>{entry.subTitle}</div>
                                                                    <div className="text-xs">{entry.description}</div>
                                                                </div>
                                                            </div>
                                                            {daysOfWeek(week.firstDayOfWeek).map((day, weekIndex: number) =>
                                                                <div key={weekIndex} className={`w-20 shrink-0 text-center border-solid border-b-1 font-bold ${!anyDistrictShowsWeekend() && isWeekendDay(day) ? 'hidden' : ''} ${displayFlags && isTaskFlagged(entry) && allowEdit && "hover:underline hover:underline-offset-4 cursor-pointer"} ${ isDayFlagged(day, entry) ? getColor() : ''}`}>
                                                                    <span> 
                                                                         {(getTotalsForDay(day, entry, true))}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {isTaskFlagged(entry) && (
                                                                <ExclamationCircleIcon
                                                                    className='w-8 h-8 text-red-600'
                                                                    aria-hidden='true' />
                                                            )}
                                                            {!isTaskFlagged(entry) && (
                                                                <div className='w-8'></div>
                                                            )}
                                                        </div>

                                                        {/* Expanded Entries */}
                                                        {(expandedWeek === itemIndex || expandAll) &&
                                                            <Fragment>
                                                                {entry.tasks.sort((a, b) => a.taskTypeName.valueOf()?.localeCompare(b.taskTypeName.valueOf())).map((task, i) =>
                                                                    <div key={i} className='flex justify-end mb-1'>
                                                                        <div className="h-full text-right flex-1 min-w-0">
                                                                            <div className='font-bold'>{task.taskTypeName}</div>
                                                                            {task.entries.some(x => x.recoupment) ?
                                                                                <div className='font-bold'>Recoupment</div> : task.entries.some(x => x.makeupTime) ?
                                                                                    <div className='font-bold'>Makeup Time</div> : <></>
                                                                            }
                                                                            <div className='italic'>{task.entryTypeName}</div>
                                                                            {(task.programName?.length ?? 0) > 0 && (
                                                                                <div className='bg-purple-500 text-white text-center inline-block px-2 py-0.5 rounded-lg text-sm mb-1' style={{ maxWidth: '8rem' }}>{task.programName}</div>
                                                                            )}
                                                                        </div>

                                                                        {daysOfWeek(week.firstDayOfWeek).map((day, i) =>
                                                                            <div key={i} className={`table w-20 shrink-0 text-center border-t border-gray-300 entry-number ${!anyDistrictShowsWeekend() && isWeekendDay(day) ? 'hidden' : ''}`}>
                                                                                <span className='table-cell align-middle'>
                                                                                    
                                                                                    {/* If this IS the current month display the mins if exists - zeroes are unclickable */}

                                                                                    {(Number(getTimeDisplay(task, day)) <= 0) && ("0")}
                                                                                    {((isNaN(Number(getTimeDisplay(task, day))) || Number(getTimeDisplay(task, day)) > 0)) && (
                                                                                        <span
                                                                                            /*className={displayFlags && !isTaskFlagged(item) ? '' : ((allowEdit) ? "hover:underline hover:underline-offset-4 cursor-pointer" : "")}*/
                                                                                            className={allowEdit ? "hover:underline hover:underline-offset-4 cursor-pointer" : ""}
                                                                                            onClick={() => {
                                                                                                //If we are displaying flags and there is no flag in the current week, disable editing for this week
                                                                                                //if (displayFlags && !isTaskFlagged(item)) allowEdit = false;

                                                                                                if (allowEdit) {
                                                                                                    editEntry({
                                                                                                        task: task,
                                                                                                        schoolId: entry.schoolId,
                                                                                                        districtId: entry.districtId,
                                                                                                        students: entry.students ?? [],
                                                                                                        title: entry.title,
                                                                                                        subTitle: entry.subTitle,
                                                                                                        entry: task.entries.find(e => moment(e.dateOfEntry).isSame(day)),
                                                                                                        studentData: entry.studentData
                                                                                                    });
                                                                                                }
                                                                                            }}>
                                                                                            <div className={isEntryDayFlagged(day, task) ? getColor(task.entries.find(e => moment(e.dateOfEntry).isSame(day))) : ''}
                                                                                            >
                                                                                                {getTimeDisplay(task, day)}
                                                                                            </div>
                                                                                        </span>
                                                                                    )}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        <div className="table w-8 text-center border-t border-gray-300"></div>
                                                                    </div>
                                                                )}
                                                            </Fragment>
                                                        }
                                                    </div>
                                                )
                                            })}

                                            <div className='flex justify-end px-1.5 pt-0.5 pb-1/2'>
                                                <div className='font-bold flex-1 min-w-0'>{school.title} Totals</div>
                                                {/* Days of week */}
                                                {daysOfWeek(week?.firstDayOfWeek).map((day, i) => (
                                                    <div key={i} className={`w-20 shrink-0 italic text-center ${!anyDistrictShowsWeekend() && isWeekendDay(day) ? 'hidden' : ''}`}>
                                                        <span className={isWeekFlagged(school.items!, day) ? getColor() : ""}>
                                                            {getTotalsForWeek(day, school.items!)}</span>
                                                    </div>
                                                ))}
                                                <div key={`header-actions-minutes`} className='w-8 font-bold text-center'></div>
                                            </div>
                                            <br />
                                        </div>
                                    )
                                }
                            })}

                            <div className='flex justify-end px-1.5 pt-0.5 pb-1/2'>
                                <div className='font-bold flex-1 min-w-0'>{district.title} Totals</div>
                                {/* Days of week */}
                                {daysOfWeek(week?.firstDayOfWeek).map((day, i) => (
                                    <div key={i} className={`w-20 shrink-0 italic text-center ${!anyDistrictShowsWeekend() && isWeekendDay(day) ? 'hidden' : ''}`}>
                                        <span
                                            className={isDistrictWeekFlagges(district.schools!, day) ? getColor() : ""}
                                        >
                                            {getTotalsForDistrictWeek(day, district.schools!)}</span>
                                    </div>
                                ))}
                                <div key={`header-actions-minutes`} className='w-8 font-bold text-center'></div>
                            </div>
                            <br />
                            <hr style={{ border: '1px solid black' }} />
                        </div>
                    )
                }
            })}
        </Fragment >
    )
}

export default TimesheetWeekEntries;